import React from 'react';
import { Box, Button, Card, Modal, TextField } from '@mui/material';
import { MultiSelect } from '../utilities/MultiSelect';
import {
    formatDateWithTimeAndT,
    roleSelectOptionMapper,
    rolesStringToSelectOptionFormatter,
    selectOptionBackToRoleMapper,
} from '../utilities/Utilites';
import {
    ApiRoles,
    Blog,
    GlobalState,
    MultiSelectOption,
} from '../interfaces/Interfaces';
import { TextareaAutosize as BaseTextareaAutosize } from '@mui/base/TextareaAutosize';
import { styled } from '@mui/system';
import { DateTime } from 'luxon';
import { AuthPut } from '../utilities/Routing/Requests';
import { toast } from 'react-toastify';
import ImageUploader from './ImageUploader';
import { BlobServiceClient } from '@azure/storage-blob';

export const EditBlogModal = (
    refreshBlogs: () => void,
    isOpen: boolean,
    setIsOpen: (open: boolean) => void,
    roles: Array<ApiRoles>,
    author: string,
    authToken: string,
    setFile: (newFile: any) => void,
    file: any,
    setImageUrl: (newFile: string | null) => void,
    imageUrl: string | null,
    globalState: GlobalState,
    blog?: Blog,
) => {
    const Textarea = styled(BaseTextareaAutosize)(
        () => `
            width: 100%;
            font-size: 0.875rem;
            font-weight: 400;
            line-height: 1.5;
            padding: 16.5px 14px;
            border-radius: 4px;
            border: 1px solid rgba(0, 0, 0, 0.2);

            &:hover {
            border-color: black;
            }

            &:focus {
            border-color: #f44336;
            box-shadow: 0 0 0 1px #f44336;
            }

            // firefox
            &:focus-visible {
            outline: 0;
            }
        `,
    );
    let rolesArray: Array<MultiSelectOption> = blog?.Role
        ? rolesStringToSelectOptionFormatter(blog.Role, roles)
        : [];
    let title = blog?.Title || '';
    let content = blog?.Content || '';
    let newImageUrl: string | null = blog?.Img_Url || null;

    const selectDataHandler = (data: Array<MultiSelectOption>) => {
        rolesArray = data;
    };
    const onClose = () => setIsOpen(false);
    const changeTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        title = value;
    };
    const changeFile = (newImage: Blob | null) => {
        setFile(newImage);
    };
    const changeImage = (newImage: string | null) => {
        setImageUrl(newImage);
        newImageUrl = newImage;
    };
    const changeContent = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { value } = e.target;
        content = value;
    };

    //Storage account credentials
    const account = globalState?.env?.account; // get the storage account name from the .env file
    const sasToken = globalState?.env?.sasToken; // get the SAS token from the .env file
    const containerName = globalState?.env?.containerName || ''; // get the container name from the .env file
    const blobServiceClient = new BlobServiceClient(
        `https://${account}.blob.core.windows.net/?${sasToken}`,
    ); // create a blobServiceClient
    const containerClient = blobServiceClient.getContainerClient(containerName); // create a containerClient

    const handleSubmitImage = async () => {
        if (!file) {
            // check if the file is selected
            // alert('Please select an image to upload');
            return;
        }
        if (!account || !sasToken || !containerName) {
            // check if the credentials are set
            alert(
                'Please make sure you have set the Azure Storage credentials in the .env file',
            );
            return;
        }
        try {
            const blobName = `${new Date().getTime()}-${file?.name || 'image'}`; // Specify a default blob name if needed
            const blobClient = containerClient.getBlockBlobClient(blobName); // get the blob client
            await blobClient
                .uploadData(file, {
                    blobHTTPHeaders: { blobContentType: file?.type },
                })
                .then(async (res) => {
                    changeImage(res?._response?.request?.url); // set the urls array to the imageUrls state
                }); // upload the image
        } catch (error) {
            console.error(error); // Handle error
        }
    };

    const editBody = async () => {
        let selectedRoles = '';
        selectOptionBackToRoleMapper(rolesArray, roles).forEach((role) => {
            selectedRoles = selectedRoles + role.distributor_Type + ',';
        });
        await handleSubmitImage();
        const dateTimeNow = DateTime.now();
        const createdDate = formatDateWithTimeAndT(dateTimeNow);
        const body = {
            Title: title,
            Author: author,
            Img_Url: newImageUrl || '',
            Content: content,
            Created_Date: createdDate,
            Role: selectedRoles,
        };
        const id = blog?.ID || 0;
        AuthPut(`/blog/update/${id}`, body, {
            'x-access-token': authToken,
        })
            .then((res) => {
                if (res?.status === 200) {
                    refreshBlogs();
                    toast.success('Blog Successfully Edited!');
                    onClose();
                }
            })
            .catch((err) => {
                toast.error(err.toString());
            });
    };

    return (
        <Modal
            open={isOpen}
            onClose={onClose}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
            }}
        >
            <Card sx={{ width: '90%', margin: '10px', overflow: 'auto' }}>
                <h2 style={{ textAlign: 'center' }}>Edit Blog Post</h2>
                <Box
                    sx={{
                        margin: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <TextField
                        required
                        fullWidth
                        id="title"
                        label="Title"
                        name="title"
                        // autoFocus
                        sx={{ marginBottom: '16px' }}
                        onChange={changeTitle}
                        defaultValue={title}
                    />
                    {/* <TextField
                        required
                        fullWidth
                        id="Img_Url"
                        label="Image Url"
                        name="Img_Url"
                        autoFocus
                        sx={{ marginBottom: '16px' }}
                        onChange={changeUrl}
                        defaultValue={url}
                    /> */}
                    <Textarea
                        style={{
                            fontFamily: 'Roboto,Helvetica,Arial,sans-serif',
                            fontWeight: '400',
                            fontSize: '1rem',
                            lineHeight: '1.4375em',
                            letterSpacing: '0.00938em',
                            color: 'rgba(0, 0, 0, 0.87)',
                            boxSizing: 'border-box',
                            position: 'relative',
                            cursor: 'text',
                            display: 'inline-flex',
                            alignItems: 'center',
                            borderRadius: '4px',
                        }}
                        aria-label="content"
                        minRows={15}
                        placeholder="Content *"
                        required
                        id="content"
                        name="content"
                        onChange={changeContent}
                        defaultValue={content}
                    />
                    <MultiSelect
                        title="Role *"
                        options={roleSelectOptionMapper(roles)}
                        dataHandler={selectDataHandler}
                        previouslySelectedOptions={rolesStringToSelectOptionFormatter(
                            blog?.Role || '',
                            roles,
                        )}
                    />
                    <ImageUploader
                        setFile={changeFile}
                        imageUrl={imageUrl}
                        file={file}
                        changeUrl={changeImage}
                    />
                    <Button
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2 }}
                        onClick={editBody}
                    >
                        Edit
                    </Button>
                </Box>
            </Card>
        </Modal>
    );
};
